/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { Input } from 'reactstrap';
import { BaseInputProps } from './InputProps';
import { TextAreaWithAIDraft } from '@features/ai/TextAreaWithAiDraft';
import { getAddonSingleElement } from './addonUtils';
import { usePopulateInputFromConnection } from '@features/assistant';
import { useAIAssistantContext } from '@features/assistant/ai-assistant/AIAssistantContext';
import { AI_MESSAGE } from '@constants/ai';
import { isExcludedFromAiProcessing } from '@utils/initiative-universal-tracker';

interface TextInputProps
  extends Pick<
    BaseInputProps,
    | 'isDisabled'
    | 'universalTracker'
    | 'valueDataData'
    | 'handleFocus'
    | 'handleValueDataChange'
    | 'placeholder'
    | 'addons'
    | 'initiativeUtr'
  > {
  valueDataData: any;
}

export default function TextInput(props: TextInputProps) {
  const {
    isDisabled,
    universalTracker,
    valueDataData,
    handleFocus,
    handleValueDataChange,
    placeholder,
    addons,
    initiativeUtr,
  } = props;

  const defaultValue = valueDataData === undefined ? '' : valueDataData;
  const { getTextDraft, canAccessAIMetricAssistant } = useAIAssistantContext();
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    return handleValueDataChange(e.target.value);
  };

  usePopulateInputFromConnection({
    inputChangeHandler: ({ value, source }) => {
      handleValueDataChange(value, source);
    },
  });

  const disabled = isDisabled?.(props);
  const maxLength = universalTracker.getMax();
  const utrId = universalTracker.getId();
  const { beforeElement, afterElement } = getAddonSingleElement(addons);

  if (maxLength && maxLength > 1 && maxLength < 100) {
    return (
      <div className='position-relative input-group mb-3'>
        {beforeElement || null}
        <Input
          autoComplete='off'
          type='text'
          className='styled-input'
          name={utrId}
          value={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          required={true}
          onFocus={handleFocus}
          onClick={handleFocus}
          onChange={onChange}
        />
        {afterElement || null}
      </div>
    );
  } else {
    return (
      <div className='position-relative input-group mb-3'>
        {beforeElement || null}
        <TextAreaWithAIDraft
          key={`inputfactor-textarea-ai-${utrId}`}
          rows={5}
          className='styled-input'
          name={utrId}
          value={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          required={true}
          onFocus={handleFocus}
          onClick={handleFocus}
          // TODO: editorState is not used?
          onChange={(data, _editorState) => handleValueDataChange(data)}
          getDraft={getTextDraft}
          canUseAIDraft={canAccessAIMetricAssistant}
          draftConfig={
            isExcludedFromAiProcessing(initiativeUtr)
              ? { disabled: true, tooltip: AI_MESSAGE.EXCLUDED_METRIC_SINGULAR }
              : undefined
          }
        />
        {afterElement || null}
      </div>
    );
  }
}
